import React from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { DisplayAuth } from '../context/display-auth'
import { Footer } from './component/footer'
import { SidebarLeft } from './component/sidebar-left'
import { RouteAuthorized } from './route/route-admin'
import { RouteDownload } from './route/route-download'
import { RoutePublic } from './route/route-public'
import { RouteRedirect } from './route/route-redirect'
import { RouteOpen } from './route/route-open'
import { RouteUnauthorized } from './route/route-unauthroized'
import { SignedInAuthRoute } from './route/route-auth-signed-in'
import { RouteDoc } from './route/route-doc'
import { LMS_LEARNER_PATHS, RouteLms } from '../view/lms-learner/route-lms'
import { SubHeader } from './component/subheader'
import { AuthHeader, UnAuthHeader } from './component/header'
import { ImpersonationBannerConnected } from './component/impersonation-banner'
import { DisplayUnauth } from '../context/display-unauth'
import { AuthRedirect } from './component/auth-redirect'
import { NotificationProvider } from '../context/notification/context'
import { TransferOutcomeToasts } from '../shared/transfer/components/transfer-outcome-toasts'
import { useAuthContext, useRefresh } from '../context/auth.context'
import { currentClaims, isRestrictedSession } from '../shared/organization-deletion/organization-deletion'
import { RestrictedShell } from './component/restricted-shell'
import { ScheduledDeletionBanner } from '../shared/banner/scheduled-deletion-banner'
import { ScheduledNotice } from '../shared/organization-deletion/scheduled-notice'
import { useLoad } from '../shared/hooks/load'
import { AlertExceptionProvider } from '../shared/alert-exception/context'
import { isAdminSection, useAdminNavigation } from '../role/hook/admin-navigation'
import { AuditRecordProvider } from '../shared/audit/history/audit-record-context'
import { LaunchBridge } from '../view-launch/launch-bridge'

// No admin navigation in this organization: no empty sidebar column.
// Someone with no admin navigation who reaches an admin section (a stale
// destination, a bookmark, a typed URL) goes to their landing instead of an
// empty admin page with no sidebar (owner retest 2, 2026-09-28). Account
// pages under /org stay reachable.
export const AdminSectionGuard = ({ children }) => {
  const { pathname } = useLocation()
  const adminNavigation = useAdminNavigation()

  if (adminNavigation.ready && !adminNavigation.has && isAdminSection(pathname)) {
    return <Redirect to="/download/links" />
  }

  return children
}

export const AuthSidebarLeft = () => {
  const adminNavigation = useAdminNavigation()

  if (!adminNavigation.has) return null

  return (
    <div className="d-none d-lg-block h-100" style={{ width: 260 }}>
      <SidebarLeft />
    </div>
  )
}

// Whether this tab holds the owner's restricted restore session. Before the
// first refresh has filled the claims, the stored token says, so a reload
// of the restore page never mounts the full shell.
export const useRestrictedSession = () => {
  const authContext = useAuthContext()
  const state = authContext.state
  return !!state.isAuth && isRestrictedSession(currentClaims(state))
}

// The shell's refresh on load. useRefresh ends the session itself on a 401
// (expired, idle, revoked from elsewhere); anything else (the network) keeps
// the session -- each request's own 401 handling still applies -- and must
// not escape as an unhandled rejection (the dev overlay's "[object Object]").
export const refreshOnLoad = async refresh => {
  try {
    await refresh()
  } catch (err) {
    console.warn('The session could not be refreshed; keeping it for now.', err)
  }
}

export const PageLayout = () => {
  const { refresh } = useRefresh()

  useLoad(() => refreshOnLoad(refresh), [])

  const restricted = useRestrictedSession()

  if (restricted) {
    return <RestrictedShell />
  }

  return (
    <div className="d-flex h-100">
      <div className="flex-shrink">
        <DisplayAuth>
          <AuthSidebarLeft />
        </DisplayAuth>
      </div>
      <div className="flex-fill d-flex flex-column h-100" style={{ minWidth: 0 }}>
        {/* Pages declare the record they show; the breadcrumb bar's History reads it. */}
        <AuditRecordProvider>
          <div className="flex-shirink">
            <DisplayUnauth>
              <UnAuthHeader />
            </DisplayUnauth>
            <DisplayAuth>
              {/* An impersonated session's "Viewing as" strip; nothing otherwise. */}
              <ImpersonationBannerConnected />
              {/* Organizations this owner can still restore; nothing otherwise. */}
              <ScheduledDeletionBanner />
              {/* A member's organization scheduled for deletion, until they acknowledge it (D5); nothing otherwise. */}
              <ScheduledNotice />
              <AuthHeader />
              <SubHeader />
            </DisplayAuth>
          </div>
          <div className="flex-fill overflow-hidden p-2">
            <NotificationProvider>
              {/* how this tab's uploads and copies ended: toasts (nothing
                  signed out: there are none) */}
              <TransferOutcomeToasts />
              <AlertExceptionProvider>
                <DisplayUnauth>
                  <Switch>
                    <Route path="/public" component={RoutePublic} />
                    <Route path="/download" component={RouteDownload} />
                    <Route path="/redirect" component={RouteRedirect} />
                    <Route path="/open" component={RouteOpen} />
                    <Route path={LMS_LEARNER_PATHS} component={RouteLms} />

                    <Route path="/auth" component={RouteUnauthorized} />
                    <Route path="/doc" component={RouteDoc} />
                    <Route path="/" component={AuthRedirect} />
                  </Switch>
                </DisplayUnauth>

                <DisplayAuth>
                  <Switch>
                    <Route path="/public" component={RoutePublic} />
                    <Route path="/download" component={RouteDownload} />
                    <Route path="/redirect" component={RouteRedirect} />
                    <Route path="/open" component={RouteOpen} />
                    <Route path={LMS_LEARNER_PATHS} component={RouteLms} />

                    <Route path="/auth" component={SignedInAuthRoute} />
                    <Route path="/doc" component={RouteDoc} />
                    {/* Open a course in the web viewer: anyone signed in, no admin role (signed out, "/" signs in and returns). */}
                    <Route path="/launch/content/:nodeID" component={LaunchBridge} />
                    <Route path="/">
                      <AdminSectionGuard>
                        <RouteAuthorized />
                      </AdminSectionGuard>
                    </Route>
                  </Switch>
                </DisplayAuth>
              </AlertExceptionProvider>
            </NotificationProvider>
          </div>
          <div className="flex-shrink">
            <Footer />
          </div>
        </AuditRecordProvider>
      </div>
    </div>
  )
}
